// ---------------------------------------------------------------------------
// พอร์ต logic ของ gs/Code.gs มาเป็น JS ฝั่งหน้าเว็บ ให้ทำงานบน Firebase Realtime Database
// ---------------------------------------------------------------------------
// gsClient.js ส่งต่อ gsGet / gsPost(action, params) มาที่ fbHandle ที่นี่เมื่อเปิดโหมด Firebase
// เลยไม่ต้องแก้ repositories / services — signature และผลลัพธ์เหมือนเดิม
//
// โครงข้อมูล: /<table>/<key(id)> = แถว (มี org_id) · ค่าที่เป็น object/array เก็บเป็น JSON string
// เหมือนที่เคยเก็บในชีต (เพื่อให้ [] / {} ไม่หายไปเพราะ RTDB ไม่เก็บ node ว่าง)
// ไม่ต้อง ensureColumns แล้ว (RTDB ไม่มี schema) และไม่มี LockService:
//   - เลขที่เอกสารใช้ fbTransact (ETag compare-and-set + retry)
//   - แถวอื่นเขียนที่ key ของตัวเอง ไม่ทับกัน
// ---------------------------------------------------------------------------
import { fbGet, fbPut, fbPatch, fbDelete, fbQueryEqual, fbTransact, encodeKey } from "./fbClient";

const FIELD = { ID: "id", ORG: "org_id", CREATED: "created_at", UPDATED: "updated_at" };

/** ตารางที่ไม่ผูกกับองค์กร (global) */
export const GLOBAL_TABLES = ["organizations", "subscriptions", "roles", "permission_groups", "settings", "users"];

/** ตารางผสม: แถวกลาง (ไม่มี org_id) + แถวของแต่ละร้าน — ดู gs/Code.gs */
export const HYBRID_TABLES = ["roles", "settings"];

export const TABLES = [
  "users", "organizations", "subscriptions", "roles", "permission_groups", "settings",
  "branches", "organization_members", "staff",
  "customers", "leads", "activities",
  "resources", "appointments",
  "products", "inventory", "stock_moves", "assets",
  "quotations", "invoices", "receipts", "payment_links", "tax_documents", "doc_sequences",
  "installment_plans", "contracts", "commissions",
  "membership_tiers", "rewards", "point_transactions", "customer_packages", "wallets",
  "chat_threads", "chat_messages", "slips", "campaigns", "sales_orders", "pos_sales",
  "field_jobs", "documents", "notifications", "faqs",
  "boost_orders", "boost_points", "addon_purchases", "custom_requests",
  "transactions",
];

export const DOC_TYPES = ["invoice", "quotation", "receipt", "tax_invoice", "receipt_tax_invoice", "credit_note",
  "customer", "appointment", "product", "pos", "sales_order"];

/** ชุดฟรีรับลูกค้าได้ไม่เกินนี้ — ต้องตรงกับ FREE_TIER.limits.customers ใน src/config/plans.js */
export const FREE_CUSTOMER_LIMIT = 5;

// ---------------------------------------------------------------------------
// helper
// ---------------------------------------------------------------------------
const lower = (v) => String(v === null || v === undefined ? "" : v).trim().toLowerCase();
const parseMaybe = (v, fallback) => (typeof v === "string" ? JSON.parse(v) : v || fallback);
const uid = (prefix) => `${prefix || "id"}_${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;

/** แถวนี้ร้าน org มองเห็นไหม */
export const visibleToOrg = (table, row, org) => {
  if (!org) return true;
  if (GLOBAL_TABLES.indexOf(table) === -1) return String(row[FIELD.ORG]) === String(org);
  if (HYBRID_TABLES.indexOf(table) !== -1) return !row[FIELD.ORG] || String(row[FIELD.ORG]) === String(org);
  return true;
};

/** ค่าที่จะเก็บ (object/array -> JSON string, null/undefined -> ไม่เก็บ) */
const encodeValue = (v) => {
  if (v instanceof Date) return v.toISOString();
  if (typeof v === "object") return JSON.stringify(v);
  if (typeof v === "number" && !Number.isFinite(v)) return null;
  return v;
};

/** แถว -> object ที่เขียนลง RTDB (ข้ามค่า null/undefined, org_id เก็บเป็น string เสมอเพื่อ query ตรง) */
export const encodeRow = (record) => {
  const out = {};
  Object.keys(record).forEach((k) => {
    const v = record[k];
    if (v === null || v === undefined) return;
    out[k] = k === FIELD.ORG ? String(v) : encodeValue(v);
  });
  return out;
};

/** ค่าที่อ่านกลับ (พยายาม parse JSON เหมือนที่เคยอ่านจากชีต) */
const decodeValue = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  if (typeof v === "string") {
    const s = v.trim();
    if ((s.charAt(0) === "{" && s.charAt(s.length - 1) === "}") || (s.charAt(0) === "[" && s.charAt(s.length - 1) === "]")) {
      try { return JSON.parse(s); } catch (e) { return v; }
    }
    if (s === "true") return true;
    if (s === "false") return false;
  }
  return v;
};

export const decodeRow = (raw) => {
  const out = {};
  Object.keys(raw || {}).forEach((k) => { out[k] = decodeValue(raw[k]); });
  return out;
};

/** ผลจาก RTDB ({key: row} หรือ array ถ้า key เป็นเลขเรียง) -> array ของแถว */
const toRows = (data) => {
  if (!data || typeof data !== "object") return [];
  const list = Array.isArray(data) ? data : Object.values(data);
  return list.filter((r) => r && typeof r === "object").map(decodeRow);
};

const rowPath = (table, id) => `${table}/${encodeKey(id)}`;

const requireTable = (table) => {
  if (!table) throw new Error("ไม่ได้ระบุ table");
  return String(table).trim();
};

/** อ่านทั้งตาราง (ถ้าส่ง org และเป็นตารางของร้าน -> query ด้วย org_id) แล้วเรียงตามเวลาสร้างให้คงลำดับเดิม */
const readTable = async (table, org) => {
  const name = requireTable(table);
  const scoped = org && GLOBAL_TABLES.indexOf(name) === -1;
  const data = scoped ? await fbQueryEqual(name, FIELD.ORG, String(org)) : await fbGet(name);
  const rows = toRows(data);
  // RTDB คืนตามลำดับ key — ชีตเดิมคืนตามลำดับที่เพิ่ม จึงเรียงตาม created_at (เสถียร) เมื่อไม่ได้สั่ง sortBy
  return rows
    .map((r, i) => [r, i])
    .sort((a, b) => String(a[0][FIELD.CREATED] || "").localeCompare(String(b[0][FIELD.CREATED] || "")) || a[1] - b[1])
    .map((x) => x[0]);
};

const readRow = async (table, id) => {
  const raw = await fbGet(rowPath(requireTable(table), id));
  return raw && typeof raw === "object" ? decodeRow(raw) : null;
};

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------
export const listRows = async (params) => {
  const where = params.where ? parseMaybe(params.where, {}) : null;
  let rows = await readTable(params.table, params.org);

  // แถวที่ไม่มี org_id ถือว่าไม่ใช่ของร้านไหน (roles/settings เป็นตารางผสม ดู HYBRID_TABLES)
  if (params.org) rows = rows.filter((r) => visibleToOrg(params.table, r, params.org));

  if (where) {
    Object.keys(where).forEach((k) => { rows = rows.filter((r) => String(r[k]) === String(where[k])); });
  }

  if (params.search) {
    const keyword = String(params.search).toLowerCase();
    const keys = params.searchKeys ? (typeof params.searchKeys === "string" ? params.searchKeys.split(",") : params.searchKeys) : null;
    rows = rows.filter((r) => {
      const target = keys || Object.keys(r);
      for (let i = 0; i < target.length; i += 1) {
        const v = r[target[i]];
        if (v !== null && v !== undefined && String(v).toLowerCase().indexOf(keyword) !== -1) return true;
      }
      return false;
    });
  }

  if (params.sortBy) {
    const dir = params.sortDir === "asc" ? 1 : -1;
    rows = rows.slice().sort((a, b) => {
      const av = a[params.sortBy];
      const bv = b[params.sortBy];
      if (av === bv) return 0;
      return (av > bv ? 1 : -1) * dir;
    });
  }

  const total = rows.length;
  const page = parseInt(params.page || 0, 10);
  const perPage = parseInt(params.perPage || 0, 10);
  if (page > 0 && perPage > 0) {
    const start = (page - 1) * perPage;
    rows = rows.slice(start, start + perPage);
  }
  return { rows, total, page: page || 1, perPage: perPage || total };
};

export const getRow = async (table, id, org) => {
  const row = await readRow(table, id);
  if (!row) return null;
  // ส่ง org มาด้วย = ขอข้อมูลของร้านนั้น ห้ามคืนแถวของร้านอื่น
  return visibleToOrg(table, row, org) ? row : null;
};

/** อ่านหลายตารางในครั้งเดียว */
export const bulkRead = async (params) => {
  const tables = typeof params.tables === "string" ? params.tables.split(",") : params.tables || [];
  const names = tables.map((t) => String(t).trim()).filter(Boolean);
  const out = {};
  await Promise.all(
    names.map(async (name) => {
      let rows = await readTable(name, params.org);
      if (params.org) rows = rows.filter((r) => visibleToOrg(name, r, params.org));
      out[name] = rows;
    })
  );
  return out;
};

export const createRow = async (table, data, org) => {
  const name = requireTable(table);
  const payload = parseMaybe(data, {});
  const now = new Date().toISOString();
  const record = { ...payload };
  if (!record[FIELD.ID]) record[FIELD.ID] = uid(name.slice(0, 3));
  if (org && !record[FIELD.ORG]) record[FIELD.ORG] = org;
  if (!record[FIELD.CREATED]) record[FIELD.CREATED] = now;
  record[FIELD.UPDATED] = now;
  await fbPut(rowPath(name, record[FIELD.ID]), encodeRow(record));
  return record;
};

/** ตารางผสม: ถ้า client ส่ง org มา แถวที่จะแก้/ลบต้องเป็นของร้านนั้นเท่านั้น */
export const assertOwnedByOrg = (table, row, org) => {
  if (!org || HYBRID_TABLES.indexOf(table) === -1) return;
  if (String(row[FIELD.ORG] || "") !== String(org)) throw new Error("ไม่มีสิทธิ์แก้ข้อมูลนี้ (ไม่ใช่ของร้านนี้)");
};

export const updateRow = async (table, id, data, org) => {
  const name = requireTable(table);
  const payload = { ...parseMaybe(data, {}) };
  const current = await readRow(name, id);
  if (!current) throw new Error(`ไม่พบข้อมูล id: ${id}`);
  assertOwnedByOrg(name, current, org);
  // ห้ามย้ายแถวข้ามร้านผ่าน update
  if (org && HYBRID_TABLES.indexOf(name) !== -1) delete payload[FIELD.ORG];
  delete payload[FIELD.ID];

  const now = new Date().toISOString();
  const patch = {};
  Object.keys(payload).forEach((k) => {
    const v = payload[k];
    patch[k] = v === null || v === undefined ? null : k === FIELD.ORG ? String(v) : encodeValue(v);
  });
  patch[FIELD.UPDATED] = now;
  await fbPatch(rowPath(name, id), patch);
  return { ...current, ...payload, [FIELD.UPDATED]: now };
};

export const removeRow = async (table, id, org) => {
  const name = requireTable(table);
  const current = await readRow(name, id);
  if (!current) return { removed: false };
  assertOwnedByOrg(name, current, org);
  await fbDelete(rowPath(name, id));
  return { removed: true };
};

/**
 * เขียนทับตาราง — ต่างจากชีตเดิมที่ล้างทั้งชีต: ถ้าส่ง org มา จะแทนที่เฉพาะแถวของร้านนั้น
 * (ไม่ล้างข้อมูลร้านอื่น) · ไม่ส่ง org = แทนที่ทั้งตาราง
 */
export const replaceTable = async (table, rows, org) => {
  const name = requireTable(table);
  const list = parseMaybe(rows, []);
  const scoped = org && GLOBAL_TABLES.indexOf(name) === -1;
  const existing = await (scoped ? fbQueryEqual(name, FIELD.ORG, String(org)) : fbGet(name));
  const updates = {};
  if (existing && typeof existing === "object") Object.keys(existing).forEach((k) => { updates[k] = null; });

  const now = new Date().toISOString();
  list.forEach((r) => {
    const record = { ...r };
    if (!record[FIELD.ID]) record[FIELD.ID] = uid(name.slice(0, 3));
    if (org && !record[FIELD.ORG]) record[FIELD.ORG] = org;
    if (!record[FIELD.CREATED]) record[FIELD.CREATED] = now;
    record[FIELD.UPDATED] = now;
    updates[encodeKey(record[FIELD.ID])] = encodeRow(record);
  });
  if (Object.keys(updates).length) await fbPatch(name, updates);
  return { written: list.length };
};

// ---------------------------------------------------------------------------
// ผู้ใช้
// ---------------------------------------------------------------------------
/** หาแถว users: login_email (unique) > email + org_id > email */
export const matchUser = (rows, payload) => {
  if (payload.login_email) {
    const hit = rows.find((r) => lower(r.login_email) === lower(payload.login_email));
    if (hit) return hit;
  }
  if (payload.org_id) {
    return rows.find((r) => lower(r.email) === lower(payload.email) && String(r[FIELD.ORG]) === String(payload.org_id)) || null;
  }
  return rows.find((r) => lower(r.email) === lower(payload.email)) || null;
};

export const upsertUser = async (data) => {
  const payload = { ...parseMaybe(data, {}) };
  if (!payload.email) throw new Error("ต้องระบุ email");
  const found = matchUser(await readTable("users"), payload);
  if (found) {
    // ไม่ทับ user_type เดิมด้วยค่าว่าง
    const patch = {};
    Object.keys(payload).forEach((k) => {
      if (payload[k] !== "" && payload[k] !== null && payload[k] !== undefined) patch[k] = payload[k];
    });
    return updateRow("users", found[FIELD.ID], patch);
  }
  if (!payload.user_type) payload.user_type = "customer";
  payload.is_active = payload.is_active === undefined ? true : payload.is_active;
  return createRow("users", payload, payload.org_id);
};

export const findUser = async (email, org) => {
  if (!email) return null;
  return matchUser(await readTable("users"), org ? { email, org_id: org } : { email });
};

export const setUserType = async (email, userType) => {
  const user = await findUser(email);
  if (!user) throw new Error(`ไม่พบผู้ใช้: ${email}`);
  return updateRow("users", user[FIELD.ID], { user_type: userType });
};

// ---------------------------------------------------------------------------
// ร้าน / สมัครสมาชิกแบบแยกร้าน
// ---------------------------------------------------------------------------
const orgPlanActive = (org) => {
  if (!org.plan || !org.planExpireAt) return false;
  return new Date(org.planExpireAt).getTime() > Date.now();
};

const findOrgByCode = async (code) => {
  if (!code) return null;
  return (await readTable("organizations")).find((r) => lower(r.code) === lower(code)) || null;
};

const findOrgById = async (id) => {
  if (!id) return null;
  const row = await readRow("organizations", id);
  if (row) return row;
  return (await readTable("organizations")).find((r) => String(r[FIELD.ID]) === String(id)) || null;
};

const countCustomers = async (orgId) => (await readTable("customers", orgId)).filter((r) => String(r[FIELD.ORG]) === String(orgId)).length;

/** ร้านชุดฟรีที่ลูกค้าเต็มโควตาแล้ว — ปิดรับสมัครผ่านลิงก์ร้าน */
export const signupFull = async (org) => {
  if (orgPlanActive(org)) return false;
  return (await countCustomers(String(org[FIELD.ID]))) >= FREE_CUSTOMER_LIMIT;
};

const shopView = async (org) => ({
  id: org[FIELD.ID],
  code: org.code,
  name: org.name,
  nameEn: org.nameEn,
  businessModel: org.businessModel,
  logoColor: org.logoColor,
  plan: org.plan,
  planExpireAt: org.planExpireAt,
  addons: org.addons,
  branding: org.branding,
  signupFull: await signupFull(org),
});

export const publicShop = async (code) => {
  const org = await findOrgByCode(code);
  return org ? shopView(org) : null;
};

export const listAccounts = async (email, userType) => {
  if (!email) return [];
  const [rows, orgs] = await Promise.all([readTable("users"), readTable("organizations")]);
  const orgName = {};
  orgs.forEach((o) => { orgName[String(o[FIELD.ID])] = o.name; });
  return rows
    .filter((r) => {
      if (lower(r.email) !== lower(email)) return false;
      if (userType && String(r.user_type) !== String(userType)) return false;
      return r.is_active !== false;
    })
    .map((r) => ({
      id: r[FIELD.ID],
      email: r.email,
      login_email: r.login_email || r.email,
      user_type: r.user_type,
      org_id: r[FIELD.ORG] || "",
      org_name: orgName[String(r[FIELD.ORG])] || "",
      customer_id: r.customer_id || "",
      name: r.name || "",
    }));
};

export const registerAdmin = async (params) => {
  const user = { ...parseMaybe(params.data, {}) };
  const org = { ...parseMaybe(params.org, {}) };
  const code = String(params.code || "").toUpperCase();
  if (!user.email) throw new Error("ต้องระบุอีเมล");
  if (!org.name) throw new Error("ต้องระบุชื่อร้าน");
  if (!code) throw new Error("ต้องระบุรหัสร้าน");
  if (await findOrgByCode(code)) throw new Error(`รหัสร้านซ้ำ: ${code}`);

  org.code = code;
  const orgRow = await createRow("organizations", org);
  user.org_id = orgRow[FIELD.ID];
  user.user_type = "admin_crm";
  user.is_active = true;
  if (!user.login_email) user.login_email = user.email;
  const userRow = await createRow("users", user, orgRow[FIELD.ID]);
  return { org: orgRow, user: userRow };
};

export const registerCustomer = async (params) => {
  const user = { ...parseMaybe(params.data, {}) };
  const code = String(params.code || "").toUpperCase();
  if (!user.email) throw new Error("ต้องระบุอีเมล");
  const org = await findOrgByCode(code);
  if (!org) throw new Error(`ไม่พบร้านค้ารหัส: ${code}`);
  const orgId = org[FIELD.ID];

  if (matchUser(await readTable("users"), { email: user.email, org_id: orgId })) {
    throw new Error(`อีเมลนี้สมัครกับร้าน ${org.name} ไว้แล้ว`);
  }
  const customers = await readTable("customers", orgId);
  const count = customers.filter((r) => String(r[FIELD.ORG]) === String(orgId)).length;
  if (!orgPlanActive(org) && count >= FREE_CUSTOMER_LIMIT) {
    throw new Error(`ร้าน ${org.name} รับสมาชิกเต็มแล้ว กรุณาติดต่อร้านโดยตรง`);
  }
  const customerRow = await createRow("customers", {
    code: `C${1001 + count}`,
    name: user.name || user.email,
    email: user.email,
    phone: user.phone || "",
    address: user.address || "",
    channel: "self_register",
    points: 0,
    totalSpend: 0,
    visits: 0,
    isActive: true,
  }, orgId);

  user.org_id = orgId;
  user.user_type = "customer";
  user.is_active = true;
  user.customer_id = customerRow[FIELD.ID];
  if (!user.login_email) user.login_email = user.email;
  const userRow = await createRow("users", user, orgId);
  return { org: await shopView(org), user: userRow, customer: customerRow };
};

// ---------------------------------------------------------------------------
// ชำระเงินออนไลน์ (หน้าสาธารณะ /pay/:code)
// ---------------------------------------------------------------------------
export const publicPay = async (code) => {
  const clean = String(code || "").trim();
  if (!/^PL[A-Za-z0-9]{4,20}$/.test(clean)) return null;

  // query ด้วย code (index) — ลองตามที่พิมพ์มา แล้วตัวพิมพ์ใหญ่/เล็ก เพราะเดิมเทียบแบบไม่สนตัวพิมพ์
  let link = null;
  const tried = [];
  for (const candidate of [clean, clean.toUpperCase(), clean.toLowerCase()]) {
    if (tried.indexOf(candidate) !== -1) continue;
    tried.push(candidate);
    link = toRows(await fbQueryEqual("payment_links", "code", candidate)).find((r) => lower(r.code) === lower(clean)) || null;
    if (link) break;
  }
  if (!link) return null;

  const org = await findOrgById(link[FIELD.ORG]);
  if (!org) return null;

  let invoice = null;
  if (link.invoiceId) {
    const row = await readRow("invoices", link.invoiceId);
    if (row && String(row[FIELD.ORG]) === String(org[FIELD.ID])) invoice = row;
  }

  let status = link.status || "active";
  if (status === "active") {
    if (invoice && invoice.status === "paid") status = "paid";
    else if (link.expireAt && new Date(link.expireAt).getTime() < Date.now()) status = "expired";
  }

  const settingRows = toRows(await fbQueryEqual("settings", FIELD.ORG, String(org[FIELD.ID])));
  const setting = settingRows.find((s) => s.key === "online_payment");
  const settingsValue = (setting && setting.value) || {};

  return {
    code: link.code,
    linkId: link[FIELD.ID],
    orgId: org[FIELD.ID],
    invoiceId: link.invoiceId || "",
    docNo: link.docNo || (invoice ? invoice.docNo : ""),
    title: link.title || "",
    amount: Number(link.amount) || 0,
    status,
    expireAt: link.expireAt || null,
    paidAt: link.paidAt || null,
    shop: {
      id: org[FIELD.ID],
      name: org.name,
      nameEn: org.nameEn,
      logoColor: org.logoColor,
      plan: org.plan,
      planExpireAt: org.planExpireAt,
      addons: org.addons,
      enabledModules: org.enabledModules,
      branding: org.branding,
    },
    settings: {
      enabled: settingsValue.enabled !== false,
      thankYou: String(settingsValue.thankYou || "").slice(0, 300),
    },
  };
};

// ---------------------------------------------------------------------------
// เลขที่เอกสารเรียง
// ---------------------------------------------------------------------------
const bangkokYear = (date = new Date()) =>
  Number(new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Bangkok", year: "numeric" }).format(date));

/** ขอเลขลำดับถัดไปของ ร้าน + ประเภท + ปี ค.ศ. (Asia/Bangkok) — compare-and-set ด้วย ETag ไม่ให้เลขซ้ำ */
export const nextSeq = async (org, docType) => {
  if (!org || !(await findOrgById(org))) throw new Error("ไม่พบร้านที่จะออกเลขที่เอกสาร");
  if (DOC_TYPES.indexOf(docType) === -1) throw new Error(`ประเภทเอกสารไม่ถูกต้อง: ${docType}`);

  const year = bangkokYear();
  const id = `${org}:${docType}:${year}`;
  let seq = 0;
  await fbTransact(rowPath("doc_sequences", id), (current) => {
    const now = new Date().toISOString();
    const row = current && typeof current === "object" ? current : null;
    seq = (Number(row && row.last) || 0) + 1;
    return {
      ...(row || { [FIELD.ID]: id, [FIELD.ORG]: String(org), [FIELD.CREATED]: now, docType, year }),
      last: seq,
      [FIELD.UPDATED]: now,
    };
  });
  return { seq, year, docType };
};

// ---------------------------------------------------------------------------
// ติดตั้ง / ข้อมูลตัวอย่าง
// ---------------------------------------------------------------------------
export const importSeed = async (payload) => {
  const data = parseMaybe(payload, {});
  const result = {};
  for (const table of Object.keys(data)) {
    result[table] = (await replaceTable(table, data[table] || [])).written;
  }
  return result;
};

export const resetData = async (keepUsers) => {
  const keep = keepUsers === true || keepUsers === "true";
  const cleared = [];
  for (const t of TABLES) {
    if (keep && t === "users") continue;
    const shallow = await fbGet(t, { shallow: "true" });
    if (shallow) {
      await fbDelete(t);
      cleared.push(t);
    }
  }
  return { cleared };
};

// ---------------------------------------------------------------------------
// router — signature เดียวกับ gsGet / gsPost (action, params)
// ---------------------------------------------------------------------------
export const fbHandle = async (action, params = {}) => {
  switch (action) {
    case "ping": return { ok: true, time: new Date().toISOString(), tables: TABLES.length };
    case "list": return listRows(params);
    case "get": return getRow(params.table, params.id, params.org);
    case "bulk": return bulkRead(params);
    case "create": return createRow(params.table, params.data, params.org);
    case "update": return updateRow(params.table, params.id, params.data, params.org);
    case "remove": return removeRow(params.table, params.id, params.org);
    case "replace": return replaceTable(params.table, params.rows, params.org);
    case "auth.register": return upsertUser(params.data);
    case "auth.user": return findUser(params.email, params.org);
    case "auth.setType": return setUserType(params.email, params.userType);
    case "auth.accounts": return listAccounts(params.email, params.userType);
    case "auth.shop": return publicShop(params.code);
    case "auth.registerAdmin": return registerAdmin(params);
    case "auth.registerCustomer": return registerCustomer(params);
    case "seq.next": return nextSeq(params.org, params.docType);
    case "pay.public": return publicPay(params.code);
    case "import": return importSeed(params.payload);
    case "reset": return resetData(params.keepUsers);
    default: throw new Error(`ไม่รู้จัก action: ${action || "(ว่าง)"}`);
  }
};
